import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Grid, 
  Divider,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActionArea
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchProducts, getProductImageUrl, formatCurrency } from '../../services/productService';

/**
 * Modal displayed after successfully adding a product to the cart
 * 
 * Features:
 * - Confirmation of added product
 * - Options to continue shopping or go to checkout
 * - Recommended products based on category and tags
 * 
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the modal is open
 * @param {Function} props.onClose Function to close the modal
 * @param {Object} props.product The product that was added to the cart
 * @param {Object|string} props.options Selected options for the product
 * @param {number} props.quantity Quantity of the product added
 * @param {string} props.personalizationText Personalization text for the product
 * @returns {JSX.Element} The rendered component
 */
const AddToCartSuccessModal = ({ open, onClose, product, options, quantity, personalizationText }) => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Format options to display readable values instead of UUIDs
  const formatOptions = (optionsObj) => {
    // If no options or empty object, return null
    if (!optionsObj || (typeof optionsObj === 'object' && Object.keys(optionsObj).length === 0)) {
      return null;
    }
    
    try {
      // If options is a string, return it directly
      if (typeof optionsObj === 'string') {
        return optionsObj;
      }
      
      // Check if we have the pre-formatted display string
      if (optionsObj._display) {
        return optionsObj._display;
      }
      
      // If options is an object, process it
      if (typeof optionsObj === 'object') {
        // Array to store formatted option strings
        const formattedOptions = [];
        
        // Process each option
        for (const [key, value] of Object.entries(optionsObj)) {
          // Skip internal properties and null/undefined values
          if (key.startsWith('_') || value === null || value === undefined) {
            continue;
          }
          
          // If value is a string, format as "key: value"
          if (typeof value === 'string') {
            formattedOptions.push(`${key}: ${value}`);
            continue;
          }
          
          // If value is an object with optionName property (legacy format)
          if (typeof value === 'object' && value.optionName) {
            if (value.valueName && value.valueName !== 'Selected') {
              formattedOptions.push(`${value.optionName}: ${value.valueName}`);
            } else {
              formattedOptions.push(value.optionName);
            }
            continue;
          }
          
          // New selector format: { value_id, title, price_modifier }
          if (typeof value === 'object' && value.title) {
            formattedOptions.push(`${key}: ${value.title}`);
            continue;
          }

          // Fallback: just use the key
          formattedOptions.push(key);
        }
        
        // If we have formatted options, join them
        if (formattedOptions.length > 0) {
          return formattedOptions.join(', ');
        }
      }
      
      return 'Customized'; // Default fallback
    } catch (error) {
      console.error('Error formatting options:', error);
      return 'Customized'; // Fallback in case of error
    }
  };

  // Fetch recommended products when the modal opens
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!open || !product) return;
      
      // Log personalization text for debugging
      try {
        setLoading(true);
        const allProducts = await fetchProducts();
        
        // Filter products by same category or tags, excluding the current product
        let relatedProducts = allProducts.filter(p => 
          p.id !== product.id && (
            (product.categories && p.categories && 
              product.categories.some(cat => p.categories.includes(cat))) ||
            (product.tags && p.tags && 
              product.tags.some(tag => p.tags.includes(tag)))
          )
        );
        
        // Limit to 3 recommendations
        relatedProducts = relatedProducts.slice(0, 3);
        setRecommendations(relatedProducts);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecommendations();
  }, [open, product, options, quantity, personalizationText]);
  
  // Handle navigation to checkout
  const handleGoToCheckout = () => {
    navigate('/checkout');
    onClose();
  };
  
  // Handle navigation to products page
  const handleContinueShopping = () => {
    navigate('/products');
    onClose();
  };
  
  // Handle closing the modal
  const handleClose = () => {
    onClose();
  };
  
  // If no product is provided, don't render
  if (!product) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'success.light', color: 'white', display: 'flex', alignItems: 'center' }}>
        <CheckCircleIcon sx={{ mr: 1 }} />
        Added to Cart Successfully
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        {/* Product Added Summary */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          {/* Product Image */}
          <Box
            component="img"
            src={getProductImageUrl(product?.images && product.images.length > 0 ? product.images[0] : null)}
            alt={product?.name || 'Product'}
            sx={{ 
              width: 80, 
              height: 80, 
              objectFit: 'cover', 
              borderRadius: 1, 
              mr: 2,
              border: '1px solid #ddd',
              backgroundColor: '#f8f8f8',
              display: 'block'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/80x80/f0f0f0/8B4513?text=KBros';
            }}
          />
          
          {/* Product Details */}
          <Box>
            <Typography variant="h6">{product?.name || 'Product'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Quantity: {quantity || 1}
            </Typography>
            
            {formatOptions(options) && (
              <Typography variant="body2" color="text.secondary">
                Options: {formatOptions(options)}
              </Typography>
            )}
            
            {/* Display personalization separately if available */}
            {personalizationText && personalizationText.trim() !== '' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Personalization: "{personalizationText}"
              </Typography>
            )}
            
            <Typography variant="body1" color="primary" fontWeight="bold">
              {formatCurrency(product?.price ? product.price * (quantity || 1) : 0)}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Recommendations */}
        <Typography variant="h6" gutterBottom>
          You might also like
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={40} />
          </Box>
        ) : recommendations.length > 0 ? (
          <Grid container spacing={2}>
            {recommendations.map((item) => (
              <Grid item xs={12} sm={4} key={item.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea 
                    component={Link} 
                    to={`/products/${item.slug || item.id}`}
                    onClick={handleClose}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={getProductImageUrl(item.images && item.images.length > 0 ? item.images[0] : null)}
                      alt={item.name}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h6" component="div" noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {item.shortDescription}
                      </Typography>
                      <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                        {formatCurrency(item.price)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
            No recommendations available
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button 
          variant="outlined" 
          onClick={handleContinueShopping}
          startIcon={<ArrowBackIcon />}
        >
          Continue Shopping
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleGoToCheckout}
          startIcon={<ShoppingCartIcon />}
        >
          Go to Checkout
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCartSuccessModal;
